import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { type components } from "@njbsoft/types";
import { useAppStore } from "@/store";
import { usePermissions } from "@/hooks/use-permissions";
import { useCreateDeviceInventory } from "@/hooks/use-device-inventory";
import { useDeviceInventorySuggest } from "@/hooks/use-device-inventory-suggest";
import { useDebounce } from "@/hooks/use-debounce";
import {
  InventoryForm,
  type InventoryValidationErrorCode,
} from "@/components/device-inventory/inventory-form";
import { InventorySuggestList } from "@/components/device-inventory/inventory-suggest-list";
import { SoftDuplicateDialog } from "@/components/device-inventory/soft-duplicate-dialog";
import { PageHeader, inputClass } from "@/components/ui";
import { useToast } from "@/hooks/use-toast";

type DeviceInventoryCreate = components["schemas"]["DeviceInventoryCreate"];
type DeviceInventorySuggestCandidate = components["schemas"]["DeviceInventorySuggestCandidate"];

export const Route = createFileRoute("/_authenticated/temporary-devices/new")({
  component: DeviceInventoryNewPage,
});

function DeviceInventoryNewPage() {
  const { t } = useTranslation(["device-inventory", "org"]);
  const orgId = useAppStore((s) => s.activeOrgId);
  const { can } = usePermissions();
  const canCreate = can("device_inventory", "create");
  const navigate = useNavigate();
  const toast = useToast();

  const [serialNumber, setSerialNumber] = useState("");
  const debouncedSerial = useDebounce(serialNumber, 250);

  const suggest = useDeviceInventorySuggest({ serial: debouncedSerial });

  const create = useCreateDeviceInventory();
  const [softDuplicateCandidates, setSoftDuplicateCandidates] = useState<
    DeviceInventorySuggestCandidate[]
  >([]);
  const [softDuplicateOpen, setSoftDuplicateOpen] = useState(false);
  const [pendingBody, setPendingBody] = useState<DeviceInventoryCreate | null>(null);
  // Tracks whether the user has acknowledged the soft-duplicate dialog
  // for the current draft. When true, the next submit sets
  // `force_create: true` and the submit-button label swaps to "Create
  // Anyway". Reset on successful create / navigation away.
  const [forceCreateAcknowledged, setForceCreateAcknowledged] = useState(false);
  // Parsed 422 listing-track coherence discriminator (Defect #8). The form
  // surfaces the mapped banner and preserves its field state (Defect #9).
  const [validationErrorCode, setValidationErrorCode] =
    useState<InventoryValidationErrorCode | null>(null);
  const [submitFailedGeneric, setSubmitFailedGeneric] = useState(false);

  const COHERENCE_CODES = new Set<InventoryValidationErrorCode>([
    "listing_listed_coherence_violation",
    "listing_unlisted_coherence_violation",
    "listing_unapproved_coherence_violation",
    "assembly_type_self_declared_coherence_violation",
  ]);

  if (!orgId) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <p className="text-muted">{t("noOrgContext", { ns: "org" })}</p>
      </div>
    );
  }
  if (!canCreate) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <p className="text-muted">{t("noOrgContext", { ns: "org" })}</p>
      </div>
    );
  }

  const handleSubmit = (body: DeviceInventoryCreate) => {
    // Clear any prior submit-failure banners before re-attempting; the form
    // keeps its own field state regardless (Defect #9).
    setValidationErrorCode(null);
    setSubmitFailedGeneric(false);
    create.mutate(body, {
      onSuccess: (row) => {
        toast.success("device-inventory:create.success");
        if (row?.id) {
          void navigate({
            to: "/temporary-devices/$id",
            params: { id: row.id },
          });
        }
      },
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409) {
          const details = err.details as
            | { error?: string; candidates?: DeviceInventorySuggestCandidate[] }
            | undefined;
          if (details?.error === "possible_duplicates" && details.candidates) {
            setSoftDuplicateCandidates(details.candidates);
            setPendingBody(body);
            setSoftDuplicateOpen(true);
            return;
          }
        }
        // 422 listing-track coherence envelope (Defect #8): surface the typed
        // banner via the form; the form state stays populated (Defect #9).
        if (err instanceof ApiError && err.status === 422) {
          const details = err.details as { error?: string } | undefined;
          const code = details?.error;
          if (code && COHERENCE_CODES.has(code as InventoryValidationErrorCode)) {
            setValidationErrorCode(code as InventoryValidationErrorCode);
            return;
          }
          setSubmitFailedGeneric(true);
          return;
        }
        toast.error("device-inventory:errors.createFailed");
      },
    });
  };

  const handleForceCreate = () => {
    if (!pendingBody) return;
    setSoftDuplicateOpen(false);
    setForceCreateAcknowledged(true);
    handleSubmit({ ...pendingBody, force_create: true });
  };

  const handleSelectExistingCandidate = (candidate: DeviceInventorySuggestCandidate) => {
    setSoftDuplicateOpen(false);
    void navigate({
      to: "/temporary-devices/$id",
      params: { id: candidate.id },
    });
  };

  return (
    <div className="mx-auto max-w-3xl p-8">
      <PageHeader title={t("device-inventory:create.title")} />

      <div className="mt-6">
        <InventoryForm
          serialNumber={serialNumber}
          serialNumberSlot={
            <>
              <input
                type="text"
                required
                value={serialNumber}
                onChange={(e) => {
                  setSerialNumber(e.target.value);
                  // Editing after a failed submit signals a correction attempt;
                  // clear the stale banner while the populated form state stays
                  // intact (Defect #9 — the form is never remounted).
                  if (validationErrorCode || submitFailedGeneric) {
                    setValidationErrorCode(null);
                    setSubmitFailedGeneric(false);
                  }
                }}
                className={inputClass}
              />
              <InventorySuggestList
                candidates={suggest.data?.candidates ?? []}
                isLoading={suggest.isLoading && suggest.isFetching}
                isError={suggest.isError}
                active={debouncedSerial.trim().length >= 2}
                onSelect={handleSelectExistingCandidate}
              />
            </>
          }
          isSubmitting={create.isPending}
          onSubmit={handleSubmit}
          onCancel={() => void navigate({ to: "/temporary-devices" })}
          forceCreate={forceCreateAcknowledged}
          submitLabelKey={forceCreateAcknowledged ? "create.submitForce" : "create.submit"}
          submitErrorCode={validationErrorCode}
          submitFailedGeneric={submitFailedGeneric}
        />
      </div>

      <SoftDuplicateDialog
        open={softDuplicateOpen}
        candidates={softDuplicateCandidates}
        onForceCreate={handleForceCreate}
        onSelectExisting={handleSelectExistingCandidate}
        onCancel={() => setSoftDuplicateOpen(false)}
      />
    </div>
  );
}
